/**
 * OperatorsPage — Task 25.3 (DEC-114). PLATFORM_ADMIN list of platform operators.
 *
 * Search (name + display name), a filter bar (state, producer/disposal, active,
 * missing-abbreviation, has-duplicates, merged only), and a table with amber badges for the
 * "No abbreviation" and "Possible duplicate" curation cues. A merged-away row's status reads
 * "Merged into <kept operator>" (Corbin, 2026-10-30). Row → operator detail.
 */
import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDebouncedValue } from '@lodex-pro/web-shared';
import { listFrom, sortRows, useUrlFlag, useUrlParam, useUrlSort } from '../../lib/url-state';
import { SortableHead } from 'src/components/ui/sortable-head';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from 'src/components/ui/table';
import { Button } from 'src/components/ui/button';
import { AddButton, Pill, searchInputStyle, subText, fieldStyle } from '../../ui/bits';
import { useOperators, type OperatorFilters, type OperatorSummary } from './hooks';
import { AddOperatorModal } from './AddOperatorModal';
import { useOperatorDuplicates } from './dedupe-hooks';
import { formatNumber } from '@lodex-pro/shared/number-format';

type Kind = 'all' | 'producer' | 'disposal';

export const MERGED_ONLY_TITLE =
  "Duplicates that were merged into another operator. They're kept as aliases so old records still point somewhere.";

function TypeBadges({ o }: { o: OperatorSummary }): React.JSX.Element {
  return (
    <span style={{ display: 'inline-flex', gap: '4px' }}>
      {o.isProducer && <Pill label="Producer" tone="accent" />}
      {o.isDisposal && <Pill label="Disposal" tone="accent" />}
      {!o.isProducer && !o.isDisposal && <span style={subText}>—</span>}
    </span>
  );
}

/** The columns the Operators table sorts by (OPEN-204); anything else in ?sort= is ignored. */
const OPERATOR_SORTS = [
  'name',
  'displayName',
  'abbreviation',
  'state',
  'wells',
  'haulers',
] as const;

export function OperatorsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  // Corbin 2026-10-20: the search and filters live in the URL, so back, refresh and a shared link
  // restore them (lib/url-state.ts).
  const [search, setSearch] = useUrlParam('q');
  const [state, setState] = useUrlParam('state');
  const [kindParam, setKind] = useUrlParam('type', 'all');
  const kind = kindParam as Kind;
  const [activeParam, setActiveOnly] = useUrlParam('status', 'all');
  const activeOnly = activeParam as 'all' | 'active' | 'inactive';
  const [missingAbbrev, setMissingAbbrev] = useUrlFlag('noAbbrev');
  const [hasDuplicates, setHasDuplicates] = useUrlFlag('dupes');
  const [mergedOnly, setMergedOnly] = useUrlFlag('merged');
  const [modalOpen, setModalOpen] = React.useState(false);

  // OPEN-194: the box shows every keystroke, but the request waits for a pause in typing, so
  // "devo" is one search, not four (each one reads the whole catalog's duplicate groups).
  const q = useDebouncedValue(search.trim());

  const filters: OperatorFilters = {
    ...(q !== '' && { q }),
    ...(state.trim() !== '' && { state: state.trim().toUpperCase() }),
    ...(kind === 'producer' && { isProducer: true }),
    ...(kind === 'disposal' && { isDisposal: true }),
    ...(activeOnly === 'active' && { isActive: true }),
    ...(activeOnly === 'inactive' && { isActive: false }),
    ...(missingAbbrev && { hasAbbreviation: false }),
    ...(hasDuplicates && { hasDuplicates: true }),
    ...(mergedOnly && { mergedOnly: true }),
    sort: 'name',
  };

  const { data, isLoading, isError } = useOperators(filters);
  const duplicates = useOperatorDuplicates();
  // OPEN-204 — click a header to sort. The whole list is loaded (Corbin, 2026-11-01: sort the
  // ~7k rows in the browser rather than page them), so this sorts every operator, not a page.
  const { sort, toggle } = useUrlSort(OPERATOR_SORTS, { key: 'name', dir: 'asc' }, []);
  const operators = sortRows(data?.operators ?? [], sort, (o, k) => {
    switch (k) {
      case 'name':
        return o.name;
      case 'displayName':
        return o.displayName;
      case 'abbreviation':
        return o.abbreviation;
      case 'state':
        return o.states.join(', ');
      case 'wells':
        return o.wellCount;
      case 'haulers':
        return o.haulerCount;
    }
  });

  return (
    <div style={{ padding: '24px', maxWidth: '1150px', margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>Platform Operators</h2>
          <p style={{ ...subText, marginTop: '2px' }}>{operators.length} shown</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Button
            type="button"
            variant="outline"
            onClick={(): void =>
              void navigate('/operators/duplicates', { state: listFrom(location) })
            }
          >
            Review duplicates
            {duplicates.data !== undefined ? ` (${String(duplicates.data.groups.length)})` : ''}
          </Button>
          <AddButton label="Add Operator" onClick={(): void => setModalOpen(true)} />
        </div>
      </div>

      {/* Search + filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={(e): void => setSearch(e.target.value)}
          placeholder="Search name or display name…"
          style={{ ...searchInputStyle, maxWidth: '280px' }}
          aria-label="Search operators"
        />
        <input
          value={state}
          onChange={(e): void => setState(e.target.value.toUpperCase())}
          placeholder="State"
          maxLength={2}
          style={{ ...fieldStyle, maxWidth: '80px', minHeight: '38px', textTransform: 'uppercase' }}
          aria-label="Filter by state"
        />
        <select
          value={kind}
          onChange={(e): void => setKind(e.target.value)}
          style={{ ...fieldStyle, maxWidth: '150px', minHeight: '38px' }}
          aria-label="Producer or disposal"
        >
          <option value="all">Producer/Disposal</option>
          <option value="producer">Producer</option>
          <option value="disposal">Disposal</option>
        </select>
        <select
          value={activeOnly}
          onChange={(e): void => setActiveOnly(e.target.value)}
          style={{ ...fieldStyle, maxWidth: '130px', minHeight: '38px' }}
          aria-label="Active status"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <Button
          type="button"
          variant={missingAbbrev ? 'default' : 'outline'}
          onClick={(): void => setMissingAbbrev(!missingAbbrev)}
          style={
            missingAbbrev
              ? { backgroundColor: 'var(--color-accent-blue)', color: 'var(--color-on-accent)' }
              : undefined
          }
        >
          Missing abbreviation
        </Button>
        <Button
          type="button"
          variant={hasDuplicates ? 'default' : 'outline'}
          onClick={(): void => setHasDuplicates(!hasDuplicates)}
          style={
            hasDuplicates
              ? { backgroundColor: 'var(--color-accent-blue)', color: 'var(--color-on-accent)' }
              : undefined
          }
        >
          Has duplicates
        </Button>
        <Button
          type="button"
          variant={mergedOnly ? 'default' : 'outline'}
          onClick={(): void => setMergedOnly(!mergedOnly)}
          aria-pressed={mergedOnly}
          title={MERGED_ONLY_TITLE}
          style={
            mergedOnly
              ? { backgroundColor: 'var(--color-accent-blue)', color: 'var(--color-on-accent)' }
              : undefined
          }
        >
          Merged only
        </Button>
      </div>

      {isLoading && <p style={subText}>Loading operators…</p>}
      {isError && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Could not load operators. Refresh to try again.
        </p>
      )}

      {!isLoading && !isError && (
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHead column="name" sort={sort} onSort={toggle}>
                Name
              </SortableHead>
              <SortableHead column="displayName" sort={sort} onSort={toggle}>
                Display Name
              </SortableHead>
              <SortableHead column="abbreviation" sort={sort} onSort={toggle}>
                Abbrev
              </SortableHead>
              <SortableHead column="state" sort={sort} onSort={toggle}>
                State
              </SortableHead>
              <TableHead>Type</TableHead>
              <SortableHead column="wells" sort={sort} onSort={toggle}>
                Wells
              </SortableHead>
              <SortableHead column="haulers" sort={sort} onSort={toggle}>
                Haulers
              </SortableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {operators.map((o) => (
              <TableRow
                key={o.id}
                onClick={(): void =>
                  void navigate(`/operators/${o.id}`, { state: listFrom(location) })
                }
                style={{ cursor: 'pointer' }}
              >
                <TableCell style={{ fontWeight: 500 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    {o.name}
                    {o.mergedInto === null && o.hasDuplicates && (
                      <Pill label="Possible duplicate" tone="warning" />
                    )}
                  </span>
                </TableCell>
                <TableCell style={subText}>{o.displayName ?? '—'}</TableCell>
                <TableCell>
                  {o.abbreviation ?? <Pill label="No abbreviation" tone="warning" />}
                </TableCell>
                <TableCell>
                  {/* Every state it operates in: its own, its wells', and rows merged into it. */}
                  {o.states.length > 0 ? o.states.join(', ') : <span style={subText}>—</span>}
                </TableCell>
                <TableCell>
                  <TypeBadges o={o} />
                </TableCell>
                <TableCell>{formatNumber(o.wellCount)}</TableCell>
                <TableCell>{formatNumber(o.haulerCount)}</TableCell>
                <TableCell>
                  {o.mergedInto !== null ? (
                    <Link
                      to={`/operators/${o.mergedInto.id}`}
                      state={listFrom(location)}
                      onClick={(e): void => e.stopPropagation()}
                      style={{ fontSize: '13px', color: 'var(--color-link)' }}
                    >
                      Merged into {o.mergedInto.name}
                    </Link>
                  ) : (
                    <Pill
                      label={o.isActive ? 'Active' : 'Inactive'}
                      tone={o.isActive ? 'positive' : 'muted'}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
            {operators.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} style={{ ...subText, textAlign: 'center', padding: '32px' }}>
                  No operators match your filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <AddOperatorModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onCreated={(id): void => void navigate(`/operators/${id}`)}
      />
    </div>
  );
}
